// University at Buffalo (SUNY): public college page, official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UB's Common Data Set 2025-2026 (CDS_2025-26_v2.pdf), posted by the
//    Office of Institutional Analysis: C1 applicants, admits and enrollees for
//    Fall 2025 with the residency table, C8 test policy, C21/C22 plans. The
//    CDS's G1/G5 costs are for 2025-2026, so the cost table uses the
//    financial aid office's 2026-2027 figures instead.
//  - UB Undergraduate Admissions: First-Year (requirements, test-optional
//    FAQ, early action), Important Dates and Deadlines (fall entry table) and
//    Special Requirements for Certain Majors. The pages carry no entry year;
//    they are the pages as posted for the application that opened August 1,
//    2026. UB publishes no decision dates; the CDS gives the early action
//    notification date as "Ongoing basis".
//  - UB Financial Aid, Cost of Attendance, Undergraduate 2026-2027 (on-campus
//    housing column, New York State residents and non-residents). The three
//    required fees (Academic Excellence $626, Student Activity Fee $218,
//    Comprehensive Fee $3,122) are summed into one row; the $72 federal
//    Direct Loan origination fee is UB's own line. The rows add to UB's
//    totals ($36,530 and $58,530).
//  - College Scorecard, school id 196088 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UB Common Data Set 2025-2026',
        url: 'https://www.buffalo.edu/content/dam/www/oia/Common-Data-Sets/CDS_2025-26_v2.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UB Undergraduate Admissions, First-Year',
        url: 'https://www.buffalo.edu/admissions/apply/first-year.html',
        read: '2026-10-01',
    },
    dates: {
        label: 'UB Undergraduate Admissions, Important Dates and Deadlines',
        url: 'https://www.buffalo.edu/admissions/apply/deadlines.html',
        read: '2026-10-01',
    },
    specialMajors: {
        label: 'UB Undergraduate Admissions, Special Requirements for Certain Majors',
        url: 'https://www.buffalo.edu/admissions/apply/special-majors.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'UB Financial Aid, Cost of Attendance 2026-2027',
        url: 'https://www.buffalo.edu/financialaid/costs/costs.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?196088-University-at-Buffalo',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-at-buffalo-suny',
    kbId: 'university_at_buffalo_suny',
    name: 'University at Buffalo',
    shortName: 'UB',
    aliases: ['UB', 'SUNY Buffalo', 'Buffalo'],
    location: 'Buffalo, NY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UB brand identity, color); decoration only.
    branding: {
        primary: '#005BBB',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_at_buffalo_suny_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 45637,
        admitted: 36780,
        enrolled: 5155,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 36127, admitted: 28935 },
            { group: 'Out-of-state', applied: 4739, admitted: 3340 },
            { group: 'International', applied: 4771, admitted: 4505 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UB\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UB has no early decision plan. Its early action plan is not restrictive, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You name your intended major when you apply, and for most majors the review is the same. These work differently:',
        rows: [
            { school: 'Accounting and Business Management', requirement: 'Direct admission is subject to limited enrollment. UB recommends finishing pre-calculus and being calculus-ready before you enroll.' },
            { school: 'Theatre and Dance', requirement: 'An audition is required for acting, dance and music theatre. BFA design/technology applicants need a portfolio review and an interview.' },
            { school: 'Music', requirement: 'An audition is required for acceptance into music.' },
            { school: 'Engineering', requirement: 'Engineering applications get an additional review of your readiness in key science and math areas.' },
            { school: 'Pharmacy', requirement: 'Admitted pharmacy first-year students enter the PharmD Early Assurance Program (2+4=6). Keeping the seat takes minimum grades and GPA each semester and an interview; there are no extra high school GPA or test requirements to get in.' },
        ],
        source: 'specialMajors',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Honors College priority consideration', deadline: 'December 15', decision: null, binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: null, binding: false },
        ],
        note: 'The application opens August 1 and the fee is $50. After February 1, UB keeps taking applications until it reaches capacity. Early action brings a priority response and priority consideration for merit scholarships. The enrollment deposit is due May 1, or 10 days after acceptance if later. UB does not publish decision dates.',
        source: 'dates',
        binding: 'Early action is not binding: you do not have to commit to UB until the May 1 enrollment deposit deadline.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UB does not require SAT or ACT scores for admission or scholarship consideration. You choose on the application whether your scores are considered; if you answer no, scores you already sent are not used. Scores you do send must come directly from the testing agency.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted, and says every applicant can choose to be considered test-optional.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [7070, 29070] },
            { item: 'Required fees (academic excellence, student activity, comprehensive)', amounts: [3966, 3966] },
            { item: 'Housing (on campus)', amounts: [10376, 10376] },
            { item: 'Food (meals)', amounts: [8230, 8230] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1500, 1500] },
            { item: 'Transportation', amounts: [3078, 3078] },
            { item: 'Personal expenses', amounts: [2238, 2238] },
            { item: 'Federal loan origination fee', amounts: [72, 72] },
        ],
        totals: [36530, 58530],
        note: 'For an undergraduate living on campus, as UB\'s financial aid office budgets the full year.',
        source: 'costs',
        netPrice: {
            average: 21775,
            byIncome: [
                { income: '$0 to $30,000', amount: 15725 },
                { income: '$30,001 to $48,000', amount: 17036 },
                { income: '$48,001 to $75,000', amount: 21440 },
                { income: '$75,001 to $110,000', amount: 24611 },
                { income: 'Over $110,000', amount: 28968 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UB\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
